"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useState } from "react";

/**
 * A card form that never finishes loading looks exactly like one that is about
 * to. Both processors can land there for their own reasons - Rainforest's
 * embedded form waits on a config call and a handshake per cross-origin field
 * frame; Stripe's `PaymentElement` waits on `loadStripe`, the intent lookup and
 * its own iframes - and in both cases a single silent failure leaves a skeleton
 * on screen forever while staff wait for a patient's card to appear.
 *
 * So: give up waiting, say so, and offer a retry that remounts. Shared by both
 * adapters because the state machine is the same one either way, and because a
 * second copy would drift.
 */

/**
 * Long enough that a slow connection finishing at 8s is not interrupted, short
 * enough that nobody stares at a dead form. Matches the value
 * `rainforest-payment-method.tsx` has used in production.
 */
export const LOAD_TIMEOUT_MS = 10_000;
export interface SurfaceLoadRetry {
  /**
   * Bumped by `retry`. Include it in the surface's mount key: it is the only
   * thing other than a session change allowed to force a fresh form, and a
   * retry that reuses the broken frames is not a retry.
   */
  attempt: number;
  isLoading: boolean;
  /** The watchdog fired. Show the retry affordance, not the skeleton. */
  didNotLoad: boolean;
  markLoaded: () => void;
  markLoading: () => void;
  retry: () => void;
}

/**
 * @param resetKey Identifies the session the surface is showing - the Stripe
 *   client secret, the Rainforest payin config id. When it changes the surface
 *   remounts, and this hook has to go back to its initial state with it.
 *   Without that the watchdog would never guard a re-minted session: the hook
 *   lives above the mount boundary, so it would still be holding
 *   `hasEverLoaded` from the session that just went away, and a second form
 *   that failed to load would sit on a skeleton forever.
 */
export function useSurfaceLoadRetry(resetKey) {
  const $ = _c(15);
  const [isLoading, setIsLoading] = useState(true);
  const [didNotLoad, setDidNotLoad] = useState(false);
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [seenKey, setSeenKey] = useState(resetKey);
  if (resetKey !== seenKey) {
    setSeenKey(resetKey);
    setIsLoading(true);
    setDidNotLoad(false);
    setHasEverLoaded(false);
    setAttempt(0);
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsLoading(false);
      setDidNotLoad(false);
      setHasEverLoaded(true);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const markLoaded = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsLoading(true);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const markLoading = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setDidNotLoad(false);
      setIsLoading(true);
      setAttempt(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const retry = t2;
  let t3;
  if ($[3] !== hasEverLoaded || $[4] !== isLoading) {
    t3 = () => {
      if (!isLoading || hasEverLoaded) {
        return;
      }
      const timer = setTimeout(() => setDidNotLoad(true), LOAD_TIMEOUT_MS);
      return () => clearTimeout(timer);
    };
    $[3] = hasEverLoaded;
    $[4] = isLoading;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== attempt || $[7] !== hasEverLoaded || $[8] !== isLoading || $[9] !== resetKey) {
    t4 = [isLoading, hasEverLoaded, attempt, resetKey];
    $[6] = attempt;
    $[7] = hasEverLoaded;
    $[8] = isLoading;
    $[9] = resetKey;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== attempt || $[12] !== didNotLoad || $[13] !== isLoading) {
    t5 = {
      attempt,
      isLoading,
      didNotLoad,
      markLoaded,
      markLoading,
      retry
    };
    $[11] = attempt;
    $[12] = didNotLoad;
    $[13] = isLoading;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}
function _temp(previous) {
  return previous + 1;
}